// Session storage for the operator API key (ADR-119 v1: API keys only, no
// password login, no cookies/sessions server-side to piggyback on).
//
// Two storage tiers, operator's explicit choice at key-entry time:
//  - sessionStorage (default): cleared when the tab/window closes. This is
//    the safer default -- a stolen laptop with a closed browser tab leaks
//    nothing.
//  - localStorage ("remember on this device"): persists across restarts.
//    Explicitly opt-in, with a warning shown next to the checkbox (see
//    KeyEntryPage) because an operator API key is a bearer credential with
//    admin-equivalent power over Console (ADR-119 D2) -- anything that can
//    read localStorage on this origin (XSS, a malicious extension, another
//    process with disk access) gets it.
//
// Show-once discipline (ADR-119 D3 / brief step 3): this module is the
// ONLY place the raw key is ever written to persistent storage. Once
// CreateApiKeyResponse.plaintext_key has been shown to the operator in the
// create-key modal, it is discarded from React state (never stored here --
// storing a NEWLY MINTED key here would be a different key than the one
// authenticating the current session, and conflating the two is exactly
// the kind of bug this module exists to prevent).

const STORAGE_KEY = 'atf_console_operator_key'
const REMEMBER_FLAG_KEY = 'atf_console_remember'

export type StorageTier = 'session' | 'local'

// ---------------------------------------------------------------------------
// Cookie-session (password / OIDC login) state — ADR-118/ADR-119 Phase 7.
//
// The operator PASSWORD login (services/console/server/session.go) mints an
// HttpOnly `console_session` cookie the browser sends automatically, plus a
// CSRF token. Unlike the API key, NONE of this is written to localStorage /
// sessionStorage:
//
//   - The session token lives ONLY in the HttpOnly cookie — the SPA cannot
//     (and must not) read it; the browser attaches it on every same-origin
//     request. Storing it in JS would defeat the HttpOnly protection.
//   - The CSRF token is held in MEMORY only (this module-level variable). It
//     is re-fetchable — it is returned by /auth/login and also mirrored in the
//     non-HttpOnly `console_csrf` cookie — so persisting it buys nothing and
//     only widens its exposure. On a full page reload the in-memory copy is
//     lost; AuthProvider recovers it from the `console_csrf` cookie (see
//     readCsrfCookie) before re-probing WhoAmI, so a reloaded tab with a live
//     session cookie stays able to mutate.
//
// Mode detection: an API key in storage => 'key' mode (Bearer auth, no CSRF).
// A CSRF token in memory (or recoverable from the cookie) => 'cookie' mode
// (cookie auth + X-CSRF-Token on mutations). The client transport layer reads
// getSessionCsrf() to decide which metadata to attach.
export type AuthMode = 'key' | 'cookie' | 'none'

// csrfCookieName MUST match services/console/server/session.go's csrfCookieName.
const CSRF_COOKIE_NAME = 'console_csrf'

let sessionCsrf: string | null = null

// setSessionCsrf records the CSRF token minted by /auth/login for the current
// (cookie-authenticated) session. In-memory only, by design (see above).
export function setSessionCsrf(token: string): void {
  sessionCsrf = token
}

// getSessionCsrf returns the in-memory CSRF token, falling back to the
// non-HttpOnly console_csrf cookie the server set alongside the session cookie.
// The cookie fallback is what lets a page RELOAD (which wipes the in-memory
// copy) recover the token without forcing a re-login: the session cookie is
// still valid, and the CSRF cookie is same-origin readable exactly so the SPA
// can re-hydrate its double-submit token.
export function getSessionCsrf(): string | null {
  if (sessionCsrf) return sessionCsrf
  const fromCookie = readCsrfCookie()
  if (fromCookie) sessionCsrf = fromCookie
  return sessionCsrf
}

// clearSessionCsrf drops the in-memory CSRF token (logout, or on any auth
// failure). The HttpOnly session cookie and the console_csrf cookie are
// cleared server-side by /auth/logout (and defensively on any 401); this only
// clears the SPA's own in-memory mirror.
export function clearSessionCsrf(): void {
  sessionCsrf = null
}

// readCsrfCookie reads the non-HttpOnly console_csrf cookie. Returns null when
// absent (no cookie session) or when document.cookie is unavailable.
function readCsrfCookie(): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    const name = part.slice(0, eq).trim()
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(part.slice(eq + 1).trim()) || null
    }
  }
  return null
}

// authMode reports how the CURRENT session authenticates. A stored API key
// wins if BOTH somehow exist (key mode is the operator's explicit choice and
// uses an unambiguous Bearer header); otherwise a recoverable CSRF token means
// a cookie session; otherwise no session.
export function authMode(): AuthMode {
  if (getStoredApiKey()) return 'key'
  if (getSessionCsrf()) return 'cookie'
  return 'none'
}

function storageFor(tier: StorageTier): Storage {
  return tier === 'local' ? window.localStorage : window.sessionStorage
}

export function storeApiKey(key: string, tier: StorageTier): void {
  // Clear both tiers first so switching "remember" off on a later login
  // doesn't leave a stale copy behind in the other tier.
  window.sessionStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(REMEMBER_FLAG_KEY)

  storageFor(tier).setItem(STORAGE_KEY, key)
  if (tier === 'local') {
    window.localStorage.setItem(REMEMBER_FLAG_KEY, '1')
  }
}

export function getStoredApiKey(): string | null {
  return (
    window.sessionStorage.getItem(STORAGE_KEY) ||
    window.localStorage.getItem(STORAGE_KEY)
  )
}

// clearSession wipes the LOCAL trace of any session — the stored API key AND
// the in-memory CSRF token — so the SPA falls back to the login screen. It
// does NOT itself hit /auth/logout (that is logout()'s job, to revoke the
// server-side session row and clear the HttpOnly cookie); this is the purely
// client-side teardown used both by an explicit logout and by the 401 choke
// point in client.ts when the server has already invalidated the credential.
export function clearSession(): void {
  window.sessionStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(REMEMBER_FLAG_KEY)
  clearSessionCsrf()
}

// hasStoredSession reports whether the SPA has ANY locally-detectable session
// to probe — a stored API key (key mode) OR a recoverable CSRF token (cookie
// mode). The router's coarse client-side gate and AuthProvider's initial probe
// use this to decide whether to attempt WhoAmI at all; real enforcement is
// server-side.
export function hasStoredSession(): boolean {
  return authMode() !== 'none'
}
